import { useCallback, useRef } from "react";
import { isDoorMockEnabled } from "utils/doorMock";
import { recordDistribution, recordIncrement, startTimer, withSpan } from "utils/observability";

export async function openDoor(position: string) {
  const xpos = position[0];
  const ypos = position.substring(1);
  const url = `http://127.0.0.1:5000/?xpos=${xpos}&ypos=${ypos}`;
  const startedAt = startTimer();

  try {
    await withSpan(
      {
        op: "door.open",
        name: "door.open",
        attributes: {
          "door.position": position,
        },
      },
      async () => {
        await fetch(url, { mode: "no-cors", cache: "no-store" });
      }
    );
    const durationMs = Math.max(0, Math.round(startTimer() - startedAt));
    recordDistribution("locker.door.open.latency", durationMs, {
      result: "success",
    });
    recordIncrement("locker.door.open.count", 1, { result: "success" });
    return true;
  } catch (error) {
    if (isDoorMockEnabled()) {
      console.log(`[MOCK] Door open (${position}) - locker hardware endpoint unavailable`, error);
      const durationMs = Math.max(0, Math.round(startTimer() - startedAt));
      recordDistribution("locker.door.open.latency", durationMs, {
        result: "mock",
      });
      recordIncrement("locker.door.open.count", 1, { result: "mock" });
      return true;
    }

    const durationMs = Math.max(0, Math.round(startTimer() - startedAt));
    recordDistribution("locker.door.open.latency", durationMs, {
      result: "failure",
    });
    recordIncrement("locker.door.open.count", 1, { result: "failure" });
    return false;
  }
}

export default function useOpenDoor() {
  const lastTrigger = useRef<{ position: string; time: number } | null>(null);

  const trigger = useCallback(async (position: string, bypassDebounce = false) => {
    const now = Date.now();
    const last = lastTrigger.current;

    // Skip if same door was triggered within last 3 seconds (unless bypassing)
    if (!bypassDebounce && last?.position === position && now - last.time < 3000) {
      return true;
    }

    lastTrigger.current = { position, time: now };

    return openDoor(position);
  }, []);

  return { trigger };
}
